// AddressForm.jsx
import { useMemo, useState } from "react";
import { Modal, ModalBody, ModalHeader, Form } from "reactstrap";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

import Outlet from "./Outlet";
import AddressGeneralInformations from "./AddressGeneralInformations";
import AddressLocationTab from "./AddressLocationTab";
import {
  handleAddFormSubmit,
  handleEditFormSubmit,
  handleFormChange,
  handleEditFormCancel,
} from "../../components/modals/EditableTable";

import { computeUtcOpening, utcToLocalOpening } from "./OpeningHoursEditor";

const PATH = "address";

export default function AddressForm({
  mode, // "create" | "edit"
  data,
  setData,
  isOpen,
  setIsOpen,
  tableData,
  setTableData,
  defaultData, // required in create mode
  totalEntry, // required in create mode
  setTotalEntry, // required in create mode
}) {
  const { t } = useTranslation();
  const [errorForm, setErrorForm] = useState("");
  const [activeTab, setActiveTab] = useState("general");
  const [targetAddress, setTargetAddress] = useState("");

  const materials = useMemo(
    () => JSON.parse(localStorage.getItem("materialsDescription")) || [],
    [],
  );

  const isCreate = mode === "create";

  const toggle = () => setIsOpen(false);

  const renderTab = (label, key) => (
    <button
      type="button"
      onClick={() => setActiveTab(key)}
      className={[
        "relative pb-3 -mb-px cursor-pointer transition-colors",
        activeTab === key
          ? "text-cyan font-semibold"
          : "text-neutral-400 hover:text-neutral-600",
      ].join(" ")}
    >
      {t(label)}
      {activeTab === key && (
        <span className="absolute left-0 bottom-[-1px] h-[2px] w-full bg-cyan rounded-full" />
      )}
    </button>
  );

  const setDataFromChild = (updater) => {
    setData((prev) => {
      const next =
        typeof updater === "function" ? updater(prev) : { ...prev, ...updater };

      const tz =
        next?.openingTimezone || prev?.openingTimezone || "Europe/Paris";

      if (next && next.openingLocal) {
        const utc = computeUtcOpening(next.openingLocal, tz);
        delete next.openingLocal;
        next.opening = utc;
        next.openingTimezone = tz;
      }

      return next;
    });
  };

  /* ---------- Copy Outlet (create & edit) ---------- */
  const copyOutletData = (id) => {
    const targetId = id || data?._id;
    setTargetAddress(id);
    const address = tableData.find((item) => item._id === targetId);
    if (!address) return;

    const clonedOutlet = JSON.parse(JSON.stringify(address.outlet || {}));
    setDataFromChild({ ...data, outlet: clonedOutlet });
  };

  /* ---------- View model : fournit openingLocal dérivé de opening (UTC) pour les enfants ---------- */
  const vmData = useMemo(() => {
    const tz = data?.openingTimezone || "Europe/Paris";
    const openingUtc =
      data && typeof data.opening === "object" ? data.opening : {};
    // openingLocal est créé à la volée, NON stocké
    const openingLocal = utcToLocalOpening(openingUtc, tz);
    return { ...data, openingLocal };
  }, [data]);

  /* ---------- Submit unifié ---------- */
  const onSubmit = async (event) => {
    event.preventDefault();

    if (isCreate) {
      const response = await handleAddFormSubmit(
        event,
        data,
        setData,
        defaultData,
        tableData,
        setTableData,
        totalEntry,
        setTotalEntry,
        PATH,
        setErrorForm,
      );
      if (response?.status === 200) {
        setIsOpen(false);
        toast.success(t("The address has been successfully created"), {
          className: "custom-toast",
        });
      }
      return;
    }

    const response = await handleEditFormSubmit(data, PATH);
    if (response?.status === 200) {
      const updatedTable = tableData.map((item) =>
        item._id === data._id ? data : item,
      );
      setTableData(updatedTable);
      setIsOpen(false);
      toast.success(t("The address has been successfully edited"), {
        className: "custom-toast",
      });
    } else {
      setErrorForm(response?.data?.error || t("An error occurred"));
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} style={{ maxWidth: "70%" }}>
      <ModalHeader className="pb-0 !border-b-0 px-6 pt-6">
        <section className="flex justify-between max-md:flex-col w-full">
          <div className="flex">
            <h4 className="font-semibold text-2xl">
              {isCreate ? t("Create a new address") : t("Edit address")}
            </h4>
            {vmData?.name ? (
              <h4 className="ml-1 text-blush font-semibold text-2xl">
                | {vmData.name}
              </h4>
            ) : null}
          </div>
          <div className="flex text-base max-md:justify-end">
            <button
              className="modal-button modal-button-cancel mr-2"
              type="button"
              onClick={() => {
                if (isCreate) {
                  setIsOpen(false);
                  if (defaultData) setData(defaultData);
                } else {
                  handleEditFormCancel(setIsOpen);
                }
              }}
            >
              {t("Cancel")}
            </button>
            <button
              className="modal-button modal-button-save mr-2"
              type="submit"
              form="address-form"
            >
              {t("Save")}
            </button>
          </div>
        </section>
      </ModalHeader>

      <Form id="address-form" onSubmit={onSubmit}>
        {/* Tabs */}
        <section className="flex flex-col px-6 pt-3">
          <section className="flex max-md:flex-col text-base gap-6 border-b-2 border-neutral-200 pt-2 mb-5">
            {renderTab("General", "general")}
            {renderTab("Address", "addressGps")}
            {renderTab("Professional/industrial channel/outlet", "outlet")}
            {renderTab("Household channel/outlet", "hwOutlet")}
          </section>
        </section>

        {/* Outlet copy section */}
        {(activeTab === "outlet" || activeTab === "hwOutlet") && (
          <section className="flex flex-row items-center gap-2 ml-3">
            <span>{t("Copy outlet")}</span>
            {vmData?.name && <span className="font-bold">{vmData.name}</span>}
            <span>{t("from")}</span>
            <select
              className="w-1/3 px-2 ml-1"
              onChange={(e) => copyOutletData(e.target.value)}
              name="address"
              value={targetAddress}
            >
              <option className="px-2" value="" disabled>
                {`----------- ${t("Select address")} -----------`}
              </option>
              {tableData
                .filter((addr) => addr._id !== data._id)
                .map((addr) => (
                  <option key={addr._id} className="px-2" value={addr._id}>
                    {addr.name}
                  </option>
                ))}
            </select>
          </section>
        )}

        {/* Body */}
        <ModalBody className="pt-2 px-3">
          {activeTab === "general" && (
            <AddressGeneralInformations
              editFormData={vmData} // <- fournit openingLocal dérivé
              setEditFormData={setDataFromChild} // <- intercepte & convertit openingLocal
              handleFormChange={handleFormChange}
              errorForm={errorForm}
            />
          )}

          {activeTab === "addressGps" && (
            <AddressLocationTab
              editFormData={vmData}
              setEditFormData={setDataFromChild}
              handleFormChange={handleFormChange}
            />
          )}

          {activeTab === "outlet" && (
            <Outlet
              editFormData={vmData}
              setEditFormData={setDataFromChild}
              handleFormChange={handleFormChange}
              errorForm={errorForm}
              materials={materials.filter(
                (m) => !m.referenceName.includes("hw_"),
              )}
              activeTab={activeTab}
              targetAddress={targetAddress}
            />
          )}

          {activeTab === "hwOutlet" && (
            <Outlet
              editFormData={vmData}
              setEditFormData={setDataFromChild}
              handleFormChange={handleFormChange}
              errorForm={errorForm}
              materials={materials.filter((m) =>
                m.referenceName.includes("hw_"),
              )}
              activeTab={activeTab}
              targetAddress={targetAddress}
            />
          )}
        </ModalBody>
      </Form>
    </Modal>
  );
}
